// frontend/src/pages/Company/CompanyUsersList.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Loader2, UserPlus, Edit, Trash2, Eye, RefreshCw, Building,
  ChevronLeft, ArrowLeft, X, Shield, User, Mail, Phone, MapPin, Key, Save
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './CompanyUsersList.css';

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CompanyUsersList = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [updating, setUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchCompanyDetails();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    try {
      const res = await axios.get(`${url}/api/company/${companyId}`, {
        headers: getAuthHeaders(),
      });
      if (res.data.success) setCompany(res.data.data);
    } catch (err) {
      // silent — company info is decorative
    }
  };

  // 🔧 FIXED: Ensure users is always an array
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/company/${companyId}/users`, {
        headers: getAuthHeaders(),
      });

      if (res.data.success) {
        let usersData = res.data.data;

        // 🔧 Handle different response shapes
        if (usersData && !Array.isArray(usersData)) {
          // If it has a 'users' property that is an array
          if (Array.isArray(usersData.users)) {
            usersData = usersData.users;
          }
          // If it has a 'data' property that is an array
          else if (Array.isArray(usersData.data)) {
            usersData = usersData.data;
          }
        }

        if (Array.isArray(usersData)) {
          setUsers(usersData);
        } else {
          setUsers([]);
          setError('Invalid data format from server. Please contact support.');
        }
      } else {
        setError(res.data.message || 'Failed to load users');
        setUsers([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) return;

    try {
      setDeletingId(userId);
      await axios.delete(`${url}/api/usercomp/${userId}`, {
        headers: getAuthHeaders(),
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditFormData({
      Usercomp_sure_name: user.Usercomp_sure_name || user.name || '',
      Usercomp_email: user.Usercomp_email || user.email || '',
      Usercomp_contact: user.Usercomp_contact || user.contact || '',
      Usercomp_contact_2: user.Usercomp_contact_2 || '',   // ✅ NEW
      Usercomp_contact_3: user.Usercomp_contact_3 || '',   // ✅ NEW
      Usercomp_address: user.Usercomp_address || user.address || '',
      role: user.role || 'user',
      Usercomp_status: user.Usercomp_status || (user.isActive ? 'active' : 'inactive')
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const res = await axios.put(
        `${url}/api/usercomp/${editingUser._id}`,
        editFormData,
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        toast.success('User updated successfully');
        setEditingUser(null);
        fetchUsers();
      } else {
        toast.error(res.data.message || 'Update failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditFormData({});
  };

  const handleAddUser = () => {
    navigate(`/company/${companyId}/add-user`);
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleBack = () => {
    navigate('/ViewCompany');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const canManageUsers = () => {
    if (userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin')) return true;
    if (userType === 'company' && currentUser?.role === 'admin' && company?._id === companyId) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="company-users-container">
        <div className="loading-container">
          <Loader2 className="animate-spin" size={32} />
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="company-users-container">
      <div className="header">
        <div>
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={16} /> Back to Companies
          </button>
          <h2>Company Users</h2>
          {company && (
            <p className="company-info">
              <Building size={14} /> {company.company_name} ({company.company_code})
            </p>
          )}
        </div>
        <div className="header-actions">
          <button className="refresh-btn" onClick={handleRefresh}>
            <RefreshCw size={16} /> Refresh
          </button>
          {canManageUsers() && (
            <button className="add-button" onClick={handleAddUser}>
              <UserPlus size={16} /> Add User
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Edit Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="modal-close" onClick={handleCancelEdit}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="Usercomp_sure_name"
                  value={editFormData.Usercomp_sure_name}
                  onChange={handleEditChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="Usercomp_email"
                  value={editFormData.Usercomp_email}
                  onChange={handleEditChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact</label>
                <input
                  type="text"
                  name="Usercomp_contact"
                  value={editFormData.Usercomp_contact}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  name="Usercomp_address"
                  value={editFormData.Usercomp_address}
                  onChange={handleEditChange}
                  rows="2"
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  name="role"
                  value={editFormData.role}
                  onChange={handleEditChange}
                >
                  <option value="user">User (General)</option>
                  <option value="sales">Sales Staff</option>
                  <option value="cashier">Cashier</option>
                  <option value="purchase">Purchase Staff</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="Usercomp_status"
                  value={editFormData.Usercomp_status}
                  onChange={handleEditChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={updating}>
                  {updating ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  <div>No users found for this company.</div>
                  {canManageUsers() && (
                    <button className="add-first-btn" onClick={handleAddUser}>
                      <UserPlus size={14} /> Add First User
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr key={user._id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="user-name">
                      <strong>{user.Usercomp_sure_name || user.name}</strong>
                      {user._id === currentUser?.id && (
                        <span className="current-user-badge">You</span>
                      )}
                    </div>
                  </td>
                  <td>{user.Usercomp_email || user.email}</td>
                  <td>{user.Usercomp_contact || '-'}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.Usercomp_status === 'active' ? 'active' : 'inactive'}`}>
                      {user.Usercomp_status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(user.created_at || user.createdAt)}</td>
                  <td className="actions-cell">
                    {canManageUsers() && (
                      <>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEdit(user)}
                          title="Edit User"
                          disabled={updating}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(user._id, user.Usercomp_sure_name || user.name)}
                          disabled={deletingId === user._id || user.role === 'admin'}
                          title={user.role === 'admin' ? "Cannot delete admin user" : "Delete User"}
                        >
                          {deletingId === user._id ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                        </button>
                      </>
                    )}
                    {!canManageUsers() && (
                      <span className="no-actions">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyUsersList;
